import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDMY } from "./dateUtils";
import { assetUrl, displayUserName } from "./assetUrl";

// Fetch a logo (backend or absolute URL) and return a PNG data URL. Returns
// null if it cannot be loaded (network error, non-image, etc.).
const loadLogoDataUrl = async (url) => {
  if (!url) return null;
  const src = assetUrl(url);
  try {
    const res = await fetch(src, { cache: "no-cache", credentials: "omit" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// Try to figure out original bitmap dimensions from a data URL. Falls back to
// a reasonable default aspect ratio if it can't be determined.
const imageSize = (dataUrl) =>
  new Promise((resolve) => {
    if (!dataUrl) return resolve(null);
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });

/**
 * Export the current calculation + trips list to PDF.
 *
 * @param {object} result - calculation result from schengenCalc.calculate
 * @param {array}  trips  - list of trips
 * @param {object} opts   - { company, user }
 *   company: { name, username, logo_url, address?, phone?, website? }
 *   user:    { username, first_name, last_name }
 */
// Transliterate ijekavica diacritics to ASCII for compatibility with jsPDF's
// built-in fonts (WinAnsi encoding lacks č, ć, đ).
const asciiFold = (s) =>
  String(s || "")
    .replace(/š/g, "s").replace(/Š/g, "S")
    .replace(/đ/g, "dj").replace(/Đ/g, "Dj")
    .replace(/č/g, "c").replace(/Č/g, "C")
    .replace(/ć/g, "c").replace(/Ć/g, "C")
    .replace(/ž/g, "z").replace(/Ž/g, "Z")
    .replace(/—/g, "-").replace(/–/g, "-")
    .replace(/„|“|”/g, '"').replace(/‘|’/g, "'");

const T = (doc, text, x, yy, opts) => doc.text(asciiFold(text), x, yy, opts);

export const exportPDF = async (result, trips, opts = {}) => {
  const { company, user } = opts;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  // ---- Header: logo on the left, company name + report title on the right
  const logoData = company?.logo_url ? await loadLogoDataUrl(company.logo_url) : null;
  const logoBoxW = 80;
  const logoBoxH = 60;

  if (logoData) {
    const size = await imageSize(logoData);
    const ratio = size && size.h > 0 ? size.w / size.h : 1.6;
    let drawW = logoBoxW;
    let drawH = drawW / ratio;
    if (drawH > logoBoxH) {
      drawH = logoBoxH;
      drawW = drawH * ratio;
    }
    try {
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, y, logoBoxW, logoBoxH, 4, 4, "F");
      doc.addImage(
        logoData,
        "PNG",
        margin + (logoBoxW - drawW) / 2,
        y + (logoBoxH - drawH) / 2,
        drawW,
        drawH,
      );
    } catch {
      /* ignore image failure */
    }
  }

  const textX = logoData ? margin + logoBoxW + 16 : margin;
  doc.setTextColor(20);
  doc.setFontSize(18);
  T(doc, "Schengen 90/180 - Izvjestaj", textX, y + 20);

  if (company?.name) {
    doc.setFontSize(12);
    doc.setTextColor(60);
    T(doc, company.name, textX, y + 40);
  }
  if (company?.username) {
    doc.setFontSize(10);
    doc.setTextColor(120);
    T(doc, `@${company.username}`, textX, y + 55);
  }

  y = Math.max(y + logoBoxH, y + 60) + 18;

  // ---- Meta line: user + reference date
  doc.setFontSize(11);
  doc.setTextColor(40);
  if (user) {
    T(doc, `Korisnik: ${displayUserName(user)}`, margin, y);
    y += 15;
  }
  T(doc, `Datum provjere: ${formatDMY(result.referenceDate)}`, margin, y);
  y += 15;
  T(
    doc,
    `Aktivni 180-dnevni period: ${formatDMY(result.windowStart)} - ${formatDMY(result.windowEnd)}`,
    margin,
    y,
  );
  y += 18;

  // ---- Summary table
  autoTable(doc, {
    startY: y,
    theme: "plain",
    styles: { fontSize: 11, cellPadding: 6 },
    head: [["Metrika", "Vrijednost"]],
    body: [
      ["Iskoristeno dana", `${result.daysUsed} / 90`],
      ["Preostalo dana", `${result.remaining}`],
      ["Prekoracenje", `${result.overage}`],
      [
        "Posljednji dozvoljeni izlazak",
        result.lastAllowedExit ? formatDMY(result.lastAllowedExit) : "-",
      ],
      [
        "Status",
        result.status === "ok"
          ? "Dozvoljeno"
          : result.status === "warning"
            ? "Rizicno"
            : "Prekoracenje",
      ],
    ],
    headStyles: { fillColor: [40, 40, 40], textColor: 255 },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      data.cell.text = data.cell.text.map(asciiFold);
    },
  });

  y = doc.lastAutoTable.finalY + 24;

  // ---- Trips table
  doc.setFontSize(13);
  doc.setTextColor(20);
  T(doc, "Evidencija putovanja", margin, y);
  y += 8;

  const rows = (trips || []).map((t, i) => [
    String(i + 1),
    formatDMY(t.entry),
    t.exit ? formatDMY(t.exit) : "otvoren",
    t.exit
      ? String(
          Math.round((new Date(t.exit) - new Date(t.entry)) / 86400000) + 1,
        )
      : "-",
  ]);

  autoTable(doc, {
    startY: y + 4,
    head: [["#", "Ulazak", "Izlazak", "Trajanje"]],
    body: rows,
    styles: { fontSize: 10, cellPadding: 5 },
    headStyles: { fillColor: [40, 40, 40], textColor: 255 },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      data.cell.text = data.cell.text.map(asciiFold);
    },
  });

  // ---- Disclaimer
  const disclaimerY = doc.lastAutoTable.finalY + 30;
  doc.setFontSize(9);
  doc.setTextColor(110);
  T(
    doc,
    "Ova aplikacija sluzi kao informativni alat za tehnicki obracun pravila 90/180",
    margin,
    disclaimerY,
  );
  T(
    doc,
    "u sengenskom prostoru i ne predstavlja pravni savjet niti zvanicnu odluku nadleznih organa.",
    margin,
    disclaimerY + 12,
  );

  // Footer: page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(140);
    T(
      doc,
      `Strana ${p} / ${pageCount}`,
      pageW - margin,
      doc.internal.pageSize.getHeight() - 18,
      { align: "right" },
    );
  }

  const safeUname = (user?.username || "").replace(/[^a-z0-9._-]/gi, "");
  const filename = `schengen_${safeUname || "izvjestaj"}_${result.referenceDate}.pdf`;
  doc.save(filename);
};

export const exportJSON = (trips) => {
  const data = JSON.stringify({ version: 1, trips }, null, 2);
  const blob = new Blob([data], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `schengen_boravci_${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const importJSON = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (!parsed || !Array.isArray(parsed.trips))
          throw new Error("Nevažeći format JSON datoteke");
        const trips = parsed.trips
          .filter((t) => t && typeof t.entry === "string")
          .map((t) => ({
            id: t.id || `imp_${Math.random().toString(36).slice(2, 9)}`,
            entry: t.entry,
            exit: t.exit || null,
          }));
        resolve(trips);
      } catch (e) {
        reject(e);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
